import { FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { FiClock, FiEye, FiPrinter, FiXCircle } from "react-icons/fi";
import CustomTooltip from "../../../components/CustomTooltip";
import {
  TypeSaleStatus,
  TypeSaleStatusLabel,
  TypeSaleType,
  TypeSaleTypeLabel
} from "../../../components/enum/saleEnums";
import SaleCancelModal from "../../../components/modal/SaleCancelModal";
import SaleConfirmPaymentModal from "../../../components/modal/SaleConfirmPaymentModal";
import { DueSaleInterface } from "../../../components/modal/SaleDueContext";
import SaleListModal from "../../../components/modal/SaleListModal";
import PaginationControls from "../../../components/PaginationControls";
import Receipt from "../../../components/receipt/Receipt";
import { ToastError } from "../../../components/toaster/toast.utils";
import api from "../../../services/api";
import {
  ActionButton,
  Container,
  FilterCard,
  PaymentStatusChip,
  StatusCell,
  Table,
  TableContainer,
  Title,
  TypeCell
} from "./sale_list.styled";

interface SaleDetailItem {
  quantity: number;
  unit_price: string | number;
  subtotal: string | number;
  product: {
    product_name: string;
  };
}

interface SaleDetail {
  sale_id: number;
  sale_number: number;
  created_at: string;
  total: string | number;
  sale_type?: TypeSaleType;
  due_date?: string | null;
  client_name?: string | null;
  client_document?: string | null;
  user?: { user_name: string };
  items: SaleDetailItem[];
}

interface Sale {
  sale_id: number;
  sale_number: number;
  total: string;
  created_at: string;
  status: TypeSaleStatus;
  sale_type: TypeSaleType;
  payment_status?: "PENDING" | "OVERDUE" | "SETTLED" | "CANCELED" | null;
  client_name_snapshot?: string | null;
  user?: {
    user_name: string;
  };
}

type PaginatedSales = {
  items: Sale[];
  meta?: {
    totalPages?: number;
    totalItems?: number;
  };
};

export const SaleList: React.FC = () => {
  const [currentSales, setCurrentSales] = useState<Sale[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [totalPages, setTotalPages] = useState(1);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectSale, setSelectSale] = useState<any>(null);

  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [status, setStatus] = useState<TypeSaleStatus | "">("");

  const [cancelOpen, setCancelOpen] = useState(false);
  const [saleToCancel, setSaleToCancel] = useState<Sale | null>(null);

  const [reprintData, setReprintData] = useState<SaleDetail | null>(null);
  const [reprinting, setReprinting] = useState<number | null>(null);

  const [confirmTarget, setConfirmTarget] = useState<DueSaleInterface | null>(null);

  const fetchSales = async () => {
    try {
      const res = await api.get<PaginatedSales>(
        `/sales?page=${page}&limit=${pageSize}`,
        {
          params: {
            initial_date: startDate ? startDate : undefined,
            final_date: endDate ? endDate : undefined,
            status: status || undefined
          }
        }
      );
      const data = res.data;

      setCurrentSales(data.items || []);
      setTotalPages(data.meta?.totalPages || 1);
    } catch (err) {
      console.error("Erro ao carregar vendas:", err);
      setCurrentSales([]);
      setTotalPages(1);
    }
  };

  useEffect(() => {
    fetchSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, startDate, endDate, status]);

  const handleOpenCancel = (sale: Sale) => {
    setSaleToCancel(sale);
    setCancelOpen(true);
  };

  // Reimpressão: busca o detalhe completo da venda (com itens) e reaproveita
  // o mesmo componente Receipt/fluxo de impressão direta da Frente de Caixa
  // — útil quando o papel da térmica acaba/enrosca na hora da venda original.
  const handleReprint = async (sale: Sale) => {
    setReprinting(sale.sale_id);
    try {
      const { data } = await api.get<SaleDetail>(`/sales/${sale.sale_id}`);
      setReprintData(data);
    } catch (err) {
      console.error("Erro ao buscar venda para reimpressão:", err);
      ToastError("Não foi possível carregar a venda para reimpressão.");
    } finally {
      setReprinting(null);
    }
  };

  return (
    <Container>
      <Title>Vendas Realizadas</Title>

      <FilterCard style={{ width: "80%" }}>
        <TextField
          size="small"
          label="Data de Início"
          type="date"
          value={startDate}
          onChange={(e) => {
            setStartDate(e.target.value);
            setPage(1);
          }}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            max: new Date().toLocaleDateString("en-CA")
          }}
        />

        <TextField
          size="small"
          disabled={!startDate}
          label="Data Final"
          type="date"
          value={startDate ? endDate : ""}
          onChange={(e) => {
            setEndDate(e.target.value);
            setPage(1);
          }}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            min: startDate || undefined,
            max: new Date().toLocaleDateString("en-CA")
          }}
        />

        <FormControl size="small" style={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as TypeSaleStatus);
              setPage(1);
            }}
          >
            <MenuItem value="">Todos</MenuItem>
            {Object.values(TypeSaleStatus).map((s) => (
              <MenuItem key={s} value={s}>
                {TypeSaleStatusLabel[s]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" style={{ minWidth: 100 }}>
          <InputLabel>Por página</InputLabel>
          <Select
            label="Por página"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            <MenuItem value={8}>8</MenuItem>
            <MenuItem value={15}>15</MenuItem>
            <MenuItem value={30}>30</MenuItem>
          </Select>
        </FormControl>
      </FilterCard>

      <TableContainer>
        <Table>
          <thead>
            <tr>
              <th>Data</th>
              <th>Nº Venda</th>
              <th>Usuário</th>
              <th>Total</th>
              <th>Tipo</th>
              <th>Status</th>
              <th>Ação</th>
            </tr>
          </thead>
          <tbody>
            {currentSales.length > 0 ? (
              currentSales.map((sale) => (
                <tr key={sale.sale_id}>
                  <td>
                    {new Date(sale.created_at).toLocaleDateString("pt-BR", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric"
                    })}
                  </td>
                  <td>{sale.sale_number}</td>
                  <td>{sale.user?.user_name ?? "—"}</td>
                  <td>
                    {Number(sale.total).toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL"
                    })}
                  </td>
                  <TypeCell $saleType={sale.sale_type}>
                    {TypeSaleTypeLabel[sale.sale_type] ?? sale.sale_type}
                    {sale.sale_type === TypeSaleType.DEFERRED && sale.payment_status && sale.payment_status !== "SETTLED" && (
                      <PaymentStatusChip $overdue={sale.payment_status === "OVERDUE"}>
                        {sale.payment_status === "OVERDUE" ? "Vencida" : "Pendente"}
                      </PaymentStatusChip>
                    )}
                  </TypeCell>
                  <StatusCell status={sale.status}>
                    {TypeSaleStatusLabel[sale.status] ?? sale.status}
                  </StatusCell>
                  <td>
                    <CustomTooltip title="Exibir Detalhes">
                      <ActionButton
                        onClick={() => {
                          setSelectSale(sale.sale_id);
                          setIsModalOpen(true);
                        }}
                      >
                        <FiEye size={20} />
                      </ActionButton>
                    </CustomTooltip>
                    <CustomTooltip title="Reimprimir cupom">
                      <ActionButton
                        onClick={() => handleReprint(sale)}
                        disabled={reprinting === sale.sale_id}
                      >
                        <FiPrinter size={20} />
                      </ActionButton>
                    </CustomTooltip>
                    {sale.sale_type === TypeSaleType.DEFERRED &&
                      sale.payment_status &&
                      sale.payment_status !== "SETTLED" &&
                      sale.status === TypeSaleStatus.COMPLETED && (
                        <CustomTooltip title="Confirmar recebimento">
                          <ActionButton
                            onClick={() =>
                              setConfirmTarget({
                                sale_id: sale.sale_id,
                                sale_number: sale.sale_number,
                                financial_entry_id: 0,
                                amount: sale.total,
                                due_date: "",
                                status: sale.payment_status ?? "PENDING",
                                client_name: sale.client_name_snapshot ?? null
                              })
                            }
                          >
                            <FiClock size={20} color="#f59e0b" />
                          </ActionButton>
                        </CustomTooltip>
                      )}
                    {sale.status === TypeSaleStatus.COMPLETED && (
                      <CustomTooltip title="Cancelar venda">
                        <ActionButton onClick={() => handleOpenCancel(sale)}>
                          <FiXCircle size={20} color="#fb7185" />
                        </ActionButton>
                      </CustomTooltip>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={7}
                  style={{ textAlign: "center", padding: "1rem 0" }}
                >
                  Nenhuma venda encontrada.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </TableContainer>

      <SaleListModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectSale(null);
        }}
        sale_id={selectSale}
      />

      <SaleCancelModal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onCanceled={() => fetchSales()}
        sale={saleToCancel}
      />

      <SaleConfirmPaymentModal
        open={!!confirmTarget}
        onClose={() => setConfirmTarget(null)}
        sale={confirmTarget}
        onConfirmed={() => fetchSales()}
      />

      <PaginationControls page={page} totalPages={totalPages} setPage={setPage} />

      {reprintData &&
        ReactDOM.createPortal(
          <Receipt
            saleItems={reprintData.items.map((item) => ({
              product_name: item.product.product_name,
              quantity: item.quantity,
              unit_price: Number(item.unit_price),
              subtotal: Number(item.subtotal)
            }))}
            total={Number(reprintData.total)}
            saleNumber={reprintData.sale_number}
            createdAt={reprintData.created_at}
            cashierName={reprintData.user?.user_name}
            saleType={reprintData.sale_type}
            clientName={reprintData.client_name ?? undefined}
            clientDocument={reprintData.client_document ?? undefined}
            dueDate={reprintData.due_date ?? undefined}
            onAfterPrint={() => setReprintData(null)}
          />,
          document.body
        )}
    </Container>
  );
};

export default SaleList;
